import { useEffect, useMemo, useState } from "react";
import ModalShell from "../roster/ModalShell";
import { rosterTheme as t } from "../roster/rosterTheme";
import { calcShiftDurationHours } from "../roster/rosterUtils";
import {
  DEFAULT_SHIFT_CONFIG,
  HOSPITAL_SHIFT_TYPES,
  applyShiftTiming,
  formToPayload,
  templateToForm,
} from "../../Moduels/attendance/shiftManagementApi";

const emptyForm = () => ({
  code: "",
  name: "",
  shift_type: "morning",
  description: "",
  department_id: "",
  color_tag: "#0d9488",
  start_time: "09:00",
  end_time: "18:00",
  break_minutes: 60,
  cross_midnight: false,
  night_shift: false,
  capacity: "",
  is_active: true,
  policy_id: "",
  config_json: { ...DEFAULT_SHIFT_CONFIG },
});

function Field({ label, children }) {
  return (
    <div>
      <label className={t.label}>{label}</label>
      {children}
    </div>
  );
}

export default function ShiftFormModal({ open, onClose, template, readOnly, onSave }) {
  const [form, setForm] = useState(emptyForm);
  const [tab, setTab] = useState("basic");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = template ? templateToForm(template) : emptyForm();
    setForm(applyShiftTiming(base, {}));
    setTab("basic");
  }, [open, template]);

  const setCfg = (key, val) =>
    setForm((f) => ({ ...f, config_json: { ...f.config_json, [key]: val } }));

  const totalHours = useMemo(
    () => calcShiftDurationHours(form.start_time, form.end_time, form.break_minutes),
    [form.start_time, form.end_time, form.break_minutes]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setSaving(true);
    try {
      await onSave(formToPayload(form));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: "basic", label: "Basic" },
    { id: "timing", label: "Timing" },
    { id: "attendance", label: "Attendance rules" },
    { id: "advanced", label: "Advanced" },
  ];

  return (
    <ModalShell
      open={open}
      title={readOnly ? "View shift" : template ? "Edit shift" : "Create shift"}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="flex gap-1 border-b border-slate-100 pb-2 overflow-x-auto">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              type="button"
              onClick={() => setTab(tb.id)}
              className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap ${
                tab === tb.id ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>

        {tab === "basic" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Shift code *">
              <input className={t.input} value={form.code} disabled={readOnly || !!template} onChange={(e) => setForm({ ...form, code: e.target.value })} />
            </Field>
            <Field label="Shift name *">
              <input className={t.input} value={form.name} disabled={readOnly} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
            <Field label="Shift type">
              <select className={t.select} value={form.shift_type} disabled={readOnly} onChange={(e) => setForm({ ...form, shift_type: e.target.value })}>
                {HOSPITAL_SHIFT_TYPES.map((st) => (
                  <option key={st.value} value={st.value}>{st.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Color tag">
              <input type="color" className="h-10 w-full rounded border" value={form.color_tag} disabled={readOnly} onChange={(e) => setForm({ ...form, color_tag: e.target.value })} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description">
                <textarea className={t.input} rows={2} value={form.description} disabled={readOnly} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </Field>
            </div>
            <Field label="Status">
              <select className={t.select} value={form.is_active ? "active" : "inactive"} disabled={readOnly} onChange={(e) => setForm({ ...form, is_active: e.target.value === "active" })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
            <Field label="Capacity">
              <input type="number" className={t.input} value={form.capacity} disabled={readOnly} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
            </Field>
          </div>
        )}

        {tab === "timing" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Start time">
              <input
                type="time"
                className={t.input}
                value={form.start_time}
                disabled={readOnly}
                onChange={(e) => setForm((f) => applyShiftTiming(f, { start_time: e.target.value }))}
              />
            </Field>
            <Field label="End time">
              <input
                type="time"
                className={t.input}
                value={form.end_time}
                disabled={readOnly}
                onChange={(e) => setForm((f) => applyShiftTiming(f, { end_time: e.target.value }))}
              />
            </Field>
            <Field label="Break start">
              <input type="time" className={t.input} value={form.config_json.break_start || ""} disabled={readOnly} onChange={(e) => setCfg("break_start", e.target.value)} />
            </Field>
            <Field label="Break end">
              <input type="time" className={t.input} value={form.config_json.break_end || ""} disabled={readOnly} onChange={(e) => setCfg("break_end", e.target.value)} />
            </Field>
            <Field label="Total break (minutes)">
              <input
                type="number"
                className={t.input}
                value={form.break_minutes}
                disabled={readOnly}
                onChange={(e) => setForm((f) => applyShiftTiming(f, { break_minutes: e.target.value }))}
              />
            </Field>
            <Field label="Total shift hours">
              <div className={`${t.input} bg-slate-50 text-slate-800 font-medium`}>
                {totalHours} hrs
                <span className="ml-2 text-xs font-normal text-slate-500">(auto-calculated)</span>
              </div>
            </Field>
            <Field label="Max working hours">
              <input type="number" step="0.5" className={t.input} value={form.config_json.max_working_hours} disabled={readOnly} onChange={(e) => setCfg("max_working_hours", Number(e.target.value))} />
            </Field>
          </div>
        )}

        {tab === "attendance" && (
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Grace minutes">
              <input type="number" className={t.input} value={form.config_json.grace_minutes} disabled={readOnly} onChange={(e) => setCfg("grace_minutes", Number(e.target.value))} />
            </Field>
            <Field label="Late threshold (min)">
              <input type="number" className={t.input} value={form.config_json.late_threshold_minutes} disabled={readOnly} onChange={(e) => setCfg("late_threshold_minutes", Number(e.target.value))} />
            </Field>
            <Field label="Early exit threshold (min)">
              <input type="number" className={t.input} value={form.config_json.early_exit_threshold_minutes} disabled={readOnly} onChange={(e) => setCfg("early_exit_threshold_minutes", Number(e.target.value))} />
            </Field>
            <Field label="Half-day threshold (min)">
              <input type="number" className={t.input} value={form.config_json.half_day_threshold_minutes} disabled={readOnly} onChange={(e) => setCfg("half_day_threshold_minutes", Number(e.target.value))} />
            </Field>
            <Field label="Absent threshold (min)">
              <input type="number" className={t.input} value={form.config_json.absent_threshold_minutes} disabled={readOnly} onChange={(e) => setCfg("absent_threshold_minutes", Number(e.target.value))} />
            </Field>
            <Field label="Overtime after (hours)">
              <input type="number" step="0.5" className={t.input} value={form.config_json.overtime_threshold_hours} disabled={readOnly} onChange={(e) => setCfg("overtime_threshold_hours", Number(e.target.value))} />
            </Field>
          </div>
        )}

        {tab === "advanced" && (
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              ["cross_midnight", "Overnight shift", form.cross_midnight, (v) => setForm({ ...form, cross_midnight: v })],
              ["night_shift", "Night shift allowance", form.night_shift, (v) => setForm({ ...form, night_shift: v })],
              ["next_day_ending", "Next day ending", form.config_json.next_day_ending, (v) => setCfg("next_day_ending", v)],
              ["auto_clock_out", "Auto clock-out", form.config_json.auto_clock_out, (v) => setCfg("auto_clock_out", v)],
              ["overtime_eligible", "Overtime eligible", form.config_json.overtime_eligible, (v) => setCfg("overtime_eligible", v)],
              ["flexible_timing", "Flexible timing", form.config_json.flexible_timing, (v) => setCfg("flexible_timing", v)],
              ["rotational_eligible", "Rotational eligible", form.config_json.rotational_eligible, (v) => setCfg("rotational_eligible", v)],
              ["biometric_required", "Biometric required", form.config_json.biometric_required, (v) => setCfg("biometric_required", v)],
              ["geo_fencing_enabled", "Geo-fencing", form.config_json.geo_fencing_enabled, (v) => setCfg("geo_fencing_enabled", v)],
              ["alternate_weekends", "Alternate weekends", form.config_json.alternate_weekends, (v) => setCfg("alternate_weekends", v)],
            ].map(([key, label, val, setter]) => (
              <label key={key} className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!val} disabled={readOnly} onChange={(e) => setter(e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
        )}

        {!readOnly && (
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className={t.btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} className={t.btnPrimary}>
              {saving ? "Saving…" : "Save shift"}
            </button>
          </div>
        )}
      </form>
    </ModalShell>
  );
}
